import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';
import { formatGivenName } from '@utils/stringUtils';
import PageLayout from '@components/layout/PageLayout';
import DashboardContainer from '@components/common/DashboardContainer';
import DashboardHeader from '@components/common/DashboardHeader';
import DashboardGrid from '@components/common/DashboardGrid';
import DashboardRow from '@components/common/DashboardRow';
import DashboardCard from '@components/common/DashboardCard';
import ErrorState from '@components/common/ErrorState';
import Button from '@components/common/Button';
import { AssessorLoadingChart, SchedulerMetrics, SchedulerQuickActions } from '@components/features/dashboard';
import { dashboardService } from '@services';
import { getCurrentMonth } from '@utils/assessorLoadingChart';
import type { AssessorLoadingData, SchedulerMetricsData, SchedulerQuickActionsData } from '@app-types';

const SchedulerLaunchPage: React.FC = () => {
  const { user } = useAuth();
  const [month, setMonth] = useState(getCurrentMonth);

  // Counts refresh on every page load, not from the app-wide 5 minute cache (ADO-955, ADO-956), and never poll.
  const {
    data: metricsData,
    isLoading: metricsLoading,
    isError: metricsError,
    refetch: refetchMetrics
  } = useQuery<SchedulerMetricsData>({
    queryKey: ['scheduler-metrics'],
    queryFn: () => dashboardService.getSchedulerMetrics(),
    refetchOnMount: 'always',
    enabled: !!user
  });

  const {
    data: quickActionsData,
    isLoading: quickActionsLoading,
    isError: quickActionsError,
    refetch: refetchQuickActions
  } = useQuery<SchedulerQuickActionsData>({
    queryKey: ['scheduler-quick-actions'],
    queryFn: () => dashboardService.getSchedulerQuickActions(),
    refetchOnMount: 'always',
    enabled: !!user
  });

  // A failed month only blanks the chart, so the Scheduler can pick another month without losing the page.
  const {
    data: assessorLoadingData,
    isLoading: assessorLoadingLoading,
    isError: assessorLoadingError,
    refetch: refetchAssessorLoading
  } = useQuery<AssessorLoadingData>({
    queryKey: ['scheduler-assessor-loading', month],
    queryFn: () => dashboardService.getSchedulerAssessorLoading(month),
    refetchOnMount: 'always',
    enabled: !!user
  });

  // Each section fails on its own, so one failed request never hides the counts and chart that did load.
  const renderSectionError = (message: string, retry: () => unknown, testId: string) => (
    <ErrorState
      data-test-id={testId}
      icon={<AlertCircle size={24} />}
      message={message}
      action={
        <Button variant="secondary" size="sm" onClick={() => retry()}>
          Try again
        </Button>
      }
    />
  );

  return (
    <PageLayout>
      <DashboardContainer data-test-id="scheduler-launch-page">
        <DashboardHeader
          title={`Welcome back, ${user ? formatGivenName(user) : ''}!`}
          subtitle="Scheduling urgency and actions"
          role="Logged in as Scheduler"
          lastUpdated={`Last updated: ${new Date().toLocaleTimeString()}`}
        />
        <DashboardGrid>
          <DashboardRow>
            {metricsError ? (
              <DashboardCard>
                {renderSectionError('Failed to load the scheduling urgency metrics.', refetchMetrics, 'scheduler-metrics-error')}
              </DashboardCard>
            ) : (
              <SchedulerMetrics data={metricsData} isLoading={metricsLoading} />
            )}
          </DashboardRow>
          <DashboardRow>
            <DashboardCard title="Quick Actions">
              {quickActionsError ? (
                renderSectionError('Failed to load the Quick Action counts.', refetchQuickActions, 'scheduler-quick-actions-error')
              ) : (
                <SchedulerQuickActions data={quickActionsData} isLoading={quickActionsLoading} />
              )}
            </DashboardCard>
          </DashboardRow>
          <DashboardRow>
            <DashboardCard>
              <AssessorLoadingChart
                month={month}
                onMonthChange={setMonth}
                data={assessorLoadingData}
                isLoading={assessorLoadingLoading}
                isError={assessorLoadingError}
                onRetry={() => refetchAssessorLoading()}
              />
            </DashboardCard>
          </DashboardRow>
        </DashboardGrid>
      </DashboardContainer>
    </PageLayout>
  );
};

export default SchedulerLaunchPage;
